import type { StoreRole } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadStore } from "../../core/ops/store-ops";
import { failedLoad, loaded, loading } from "../../core/primitives/load-status";
import { shopEntryId } from "../../core/primitives/workspace";
import { CustomerQrController } from "../../sections/customer-qr/customer-qr-controller";
import { LogoFieldController } from "../../sections/logo-field/logo-field-controller";
import { LocationsSectionController } from "./sections/locations/locations-section-controller";
import { ProfileSectionController } from "./sections/profile/profile-section-controller";
import { type ExternalTab, ProvidersSectionController } from "./sections/providers/providers-section-controller";
import { SalesTaxSectionController } from "./sections/sales-tax/sales-tax-section-controller";
import { ShopAdminsSectionController } from "./sections/shop-admins/shop-admins-section-controller";
import { WebsiteSectionController } from "./sections/website/website-section-controller";
import {
  type CallbackMessage,
  SETTINGS_TABS,
  type SettingsTab,
  StoreSettingsActions,
  StoreSettingsState,
} from "./store-settings-model";

export type StoreSettingsDeps = Pick<
  AppDeps,
  | "client"
  | "authHandler"
  | "storeRepository"
  | "locationsRepository"
  | "providersRepository"
  | "countriesRepository"
  | "shareLinks"
>;

export interface StoreSettingsParams {
  storeId: string;
  tab: SettingsTab;
  /** The caller's role in the shop `storeId`, from the store list. `null` when that shop is not known: least privilege applies. */
  role: StoreRole | null;
  /** `?provider=&status=` left by a provider's hosted onboarding, read once. */
  callback: { provider: string; status: string } | null;
}

export interface StoreSettingsRouting {
  /** The tab lives in the URL so it survives a reload and can be linked to. */
  setTab(tab: SettingsTab): void;
  /** Drops the callback's own params and nothing else. */
  clearCallbackParams(): void;
  toDashboard(): void;
  /** A tab opened on the click, before the URL is known: pop-up blockers allow that and refuse a later `window.open`. */
  openBlankTab(): ExternalTab | null;
  openExternal(url: string): void;
}

export function callbackMessage(provider: string, status: string): CallbackMessage {
  const name = provider === "stripe" ? "Stripe" : "Provider";
  return {
    text:
      status === "ACTIVE"
        ? `${name} connected successfully.`
        : status === "ERROR"
          ? `${name} connection needs attention.`
          : `${name} onboarding is still incomplete.`,
    error: status === "ERROR",
  };
}

/**
 * One shop's settings: the profile, branches, website, providers and sales tax
 * on the general tab, the admins on theirs. Each section is a controller of its
 * own; this one loads the shop, mirrors it and owns the shared dialogs.
 */
export class StoreSettingsController extends SubtreeController implements StoreSettingsActions {
  readonly state: StoreSettingsState;
  readonly logo: LogoFieldController | null = null;
  readonly qr: CustomerQrController | null = null;
  readonly profile: ProfileSectionController | null = null;
  readonly locations: LocationsSectionController | null = null;
  readonly website: WebsiteSectionController | null = null;
  readonly providers: ProvidersSectionController | null = null;
  readonly salesTax: SalesTaxSectionController | null = null;
  readonly admins: ShopAdminsSectionController | null = null;

  constructor(
    readonly deps: StoreSettingsDeps,
    readonly params: StoreSettingsParams,
    readonly routing: StoreSettingsRouting,
  ) {
    super();
    const { storeId, role } = params;
    this.state = new StoreSettingsState(storeId);
    // A Manager only has the Team tab; a Staff member never sees this page at all.
    // An unknown role (`null`) gets the Manager view, the least-privileged one that is not a redirect.
    const restricted = role === "MANAGER" || role === null;
    this.state.tabs.value = restricted ? SETTINGS_TABS.filter((t) => t.id === "admins") : SETTINGS_TABS;
    this.state.tab.value = restricted ? "admins" : params.tab;
    this.subtree.put(StoreSettingsState, this.state);
    this.subtree.put(StoreSettingsActions, this);
    this.sync(() => {
      const store = this.state.store.value;
      this.state.share.value = store ? deps.shareLinks.shop(store) : null;
    }, [this.state.store]);

    if (role === "STAFF") {
      routing.toDashboard();
      return;
    }

    // General sections read and write things a Manager cannot use, so they are
    // never constructed for one, nor for an unknown role.
    const showGeneral = !restricted;
    if (showGeneral) {
      this.logo = this.own(new LogoFieldController(this.subtree, deps));
      this.qr = this.own(new CustomerQrController(this.subtree, deps));
      this.profile = this.own(
        new ProfileSectionController(this.subtree, deps, { store: this.state.store, logo: this.logo }),
      );
      this.locations = this.own(new LocationsSectionController(this.subtree, deps, { storeId }));
      this.website = this.own(new WebsiteSectionController(this.subtree, deps, { store: this.state.store }));
      this.providers = this.own(
        new ProvidersSectionController(this.subtree, deps, {
          storeId,
          openBlankTab: () => routing.openBlankTab(),
          openExternal: (url) => routing.openExternal(url),
        }),
      );
      this.salesTax = this.own(new SalesTaxSectionController(this.subtree, deps, { storeId }));
    }
    this.admins = this.own(new ShopAdminsSectionController(this.subtree, deps, { storeId, role: role ?? "MANAGER" }));

    this.sync(() => {
      this.state.store.value = deps.storeRepository.find(storeId) ?? null;
    }, [deps.storeRepository]);
    this.sync(() => {
      if (this.state.tab.value === "admins") this.admins?.ensureLoaded();
    }, [this.state.tab]);

    if (params.callback) {
      this.state.callbackMessage.value = callbackMessage(params.callback.provider, params.callback.status);
      // The controller is built during render; the URL write waits for it to finish.
      queueMicrotask(() => {
        if (!this.isDisposed) routing.clearCallbackParams();
      });
    }
    void this.#load();
  }

  selectTab = (tab: SettingsTab) => {
    if (this.state.tab.value === tab) return;
    this.state.tab.value = tab;
    this.routing.setTab(tab);
  };

  showQr = () => {
    const storeId = this.state.storeId.value;
    const { shopId, locationId } = this.deps.storeRepository;
    this.qr?.open(shopEntryId(storeId, shopId === storeId ? locationId : null));
  };

  toDashboard = () => {
    this.routing.toDashboard();
  };

  async #load(): Promise<void> {
    this.state.status.value = loading;
    const r = await loadStore({ ...this.deps, storeId: this.params.storeId });
    if (this.isDisposed) return;
    this.state.status.value = r.ok ? loaded : failedLoad(r.errors);
  }
}
